import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Modal } from 'antd';
import type { ModStatus } from '@/api/generated/schemas';

const { confirm } = Modal;

interface UseBulkModStatusProps {
  /** Refine resource string, e.g. `user_catalog.tags` */
  resource: string;
}

interface UseBulkModStatusResult {
  /** Ask for confirmation, then PATCH `{resource}/update_status`. */
  update: (ids: number[], modStatus: ModStatus, onSuccess?: () => void) => void;
  isLoading: boolean;
}

/**
 * Bulk-set `mod_status` on one or many rows of a user_catalog resource.
 * Backend endpoint: `PATCH /api/user_catalog/{resource}/update_status`
 * with body `{ ids, mod_status }` (BulkModStatusIn).
 *
 * Shared by the List (row selection) and Show (single record) views.
 */
export const useBulkModStatus = ({
  resource,
}: UseBulkModStatusProps): UseBulkModStatusResult => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { open } = useNotification();
  const {
    mutate,
    mutation: { isPending },
  } = useCustomMutation();

  // `user_catalog.tags` -> `user_catalog/tags/update_status`
  const url = `${resource.replace('.', '/')}/update_status`;

  const update = (
    ids: number[],
    modStatus: ModStatus,
    onSuccess?: () => void
  ) => {
    if (!ids.length) return;

    confirm({
      title: t('user_catalog.common.bulk_mod_status.confirm', {
        count: ids.length,
        status: t(`base.mod_status.${modStatus}`),
      }),
      okText: t('user_catalog.common.bulk_mod_status.apply'),
      cancelText: t('actions.cancel'),
      onOk: () => {
        mutate(
          {
            url,
            method: 'patch',
            values: { ids, mod_status: modStatus },
          },
          {
            onSuccess: () => {
              invalidate({ resource, invalidates: ['list', 'detail'] });
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('user_catalog.common.bulk_mod_status.success', {
                  count: ids.length,
                }),
              });
              onSuccess?.();
            },
          }
        );
      },
    });
  };

  return { update, isLoading: isPending };
};
